import React, { useState, useEffect, useRef } from "react";
import axios from "utils/axios";
import { FaPlus } from "react-icons/fa6";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import VideoContainer from "components/upload/VideoContainer";

const CHUNK_SIZE = 10 * 1024 * 1024;
let isPaused = false;

const extractS3KeyFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/\/([^/]+)$/);
  return match ? match[1] : null;
};

const ALL_ORIENTATIONS = ["portrait", "landscape", "snippet"];
const ORIENTATION_LABELS = {
  portrait: "Portrait",
  landscape: "Landscape",
  snippet: "Snippet",
};

// Which orientations this job actually needs, from the video service's
// `videoOrientations`. Set it when the purchased product is a single-
// orientation deliverable — a Basic Property Social Reel is one portrait
// video, and demanding a landscape made those jobs impossible to submit.
// Unset means the full set, so every existing job behaves exactly as before.
// The field is read straight off the lean event payload; it needs no schema
// entry. NOTE: duplicated in beleef-services-frontend and
// beleef-admin-dashboard — keep the two copies identical.
const resolveOrientations = (eventProp) => {
  const configured = (eventProp?.services || [])
    .map((svc) => svc?.videoOrientations)
    .find((list) => Array.isArray(list) && list.length > 0)
    ?.filter((o) => ALL_ORIENTATIONS.includes(o));

  return configured?.length
    ? { visible: configured, required: configured }
    : // Snippet stays optional in the default case, as it always has been.
      { visible: ALL_ORIENTATIONS, required: ["portrait", "landscape"] };
};

const VideoUploadEdit = ({ eventProp, folderPath }) => {
  const { visible: visibleOrientations, required: requiredOrientations } =
    resolveOrientations(eventProp);

  useEffect(() => {
    const online = () => {
      isPaused = false;
    };
    const offline = () => {
      isPaused = true;
    };
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, []);

  const [files, setFiles] = useState(eventProp?.editedVideos || []);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedSizes, setUploadedSizes] = useState({});
  const [totalSizes, setTotalSizes] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const previousChunkBytes = useRef({});
  const fileIdCounter = useRef(0);
  const allVideosUploaded = Object.values(uploadProgress).every(
    (p) => p === 100
  );

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + " " + sizes[i]
    );
  };

  // `loadedmetadata` can fire with 0x0 — the container parsed but no video
  // decoder was initialised (HEVC/ProRes/mkv), or rotation metadata is still
  // pending on iPhone .mov files until the first frame decodes. Keep listening
  // past metadata, and give up after a moment rather than hanging.
  const readVideoSize = (file) =>
    new Promise((resolve) => {
      const video = document.createElement("video");
      const url = URL.createObjectURL(file);
      let settled = false;

      const finish = (size) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        video.removeAttribute("src");
        video.load();
        URL.revokeObjectURL(url);
        resolve(size);
      };

      const check = () => {
        if (video.videoWidth && video.videoHeight) {
          finish({ width: video.videoWidth, height: video.videoHeight });
        }
      };

      const timer = setTimeout(() => finish(null), 5000);

      video.onloadedmetadata = check;
      video.onloadeddata = check;
      video.onresize = check;
      video.onerror = () => finish(null);

      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;
      video.src = url;
    });

  const handleFileChange = async (e, orientation) => {
    const selected = Array.from(e.target.files);
    e.target.value = "";
    if (selected.length === 0) return;

    for (const file of selected) {
      const size = await readVideoSize(file);

      // Unknown dimensions: the browser can't decode this locally, but the
      // server still can. Let it through instead of rejecting a valid video.
      if (size && orientation !== "snippet") {
        const isPortrait = size.height > size.width;
        const isLandscape = size.width > size.height;
        if (
          (orientation === "portrait" && !isPortrait) ||
          (orientation === "landscape" && !isLandscape)
        ) {
          showToast("error", `Please upload a ${orientation} video.`);
          continue;
        }
      }

      const newFile = {
        id: `${file.name}-${Date.now()}-${fileIdCounter.current++}`,
        file,
        orientation,
      };
      setFiles((prev) => [...prev, newFile]);
      setUploadProgress((prev) => ({ ...prev, [newFile.id]: 0 }));
      setUploadedSizes((prev) => ({ ...prev, [newFile.id]: 0 }));
      setTotalSizes((prev) => ({ ...prev, [newFile.id]: file.size }));
      setUploadQueue((prev) => [...prev, newFile]);
    }
  };

  useEffect(() => {
    if (uploadQueue.length > 0 && !isUploading) uploadNextVideo();
  }, [uploadQueue, isUploading]);

  const uploadNextVideo = async () => {
    if (uploadQueue.length === 0) return;
    setIsUploading(true);
    try {
      await uploadSingleVideo(uploadQueue[0]);
    } catch (error) {
      console.error("Error:", error);
    }
    setUploadQueue((prev) => prev.slice(1));
    setIsUploading(false);
  };

  const uploadSingleVideo = async (fileObj) => {
    const { id, file, orientation } = fileObj;
    previousChunkBytes.current[id] = 0;
    const { data: init } = await axios.post(
      "/admin/prepareMarketing/video/initiate",
      {
        fileName: file.name,
        fileType: file.type,
        orientation,
        propertyId: eventProp?.propertyId?._id,
        folderPath,
      }
    );
    const { uploadId, key } = init;
    const parts = [];
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
      while (isPaused) await new Promise((r) => setTimeout(r, 1000));
      const start = (partNumber - 1) * CHUNK_SIZE;
      const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
      const formData = new FormData();
      formData.append("file", chunk);
      formData.append("uploadId", uploadId);
      formData.append("key", key);
      formData.append("partNumber", partNumber);
      const { data: partRes } = await axios.post(
        "/admin/prepareMarketing/video/upload-part",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      parts.push({ PartNumber: partNumber, ETag: partRes.ETag });
      previousChunkBytes.current[id] += chunk.size;
      const uploaded = previousChunkBytes.current[id];
      setUploadedSizes((prev) => ({ ...prev, [id]: uploaded }));
      setUploadProgress((prev) => ({
        ...prev,
        [id]: Math.round((uploaded / file.size) * 100),
      }));
    }
    await axios.post("/admin/prepareMarketing/video/complete", {
      uploadId,
      videoName: file.name,
      videoSize: file.size,
      orientation,
      key,
      parts,
      eventId: eventProp?._id,
      propertyId: eventProp?.propertyId?._id,
      field: "editedVideos",
      address: eventProp?.propertyId?.address,
    });
    setFiles((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, file: { ...f.file, s3Key: key } } : f
      )
    );
    setUploadProgress((prev) => ({ ...prev, [id]: 100 }));
  };

  const handleDelete = async (fileId) => {
    const file = files.find((f) => f.id === fileId);
    if (!file?.file?.s3Key && !file?.url) {
      removeFileFromStates(fileId);
      return;
    }
    try {
      const res = await axios.post("/admin/prepareMarketing/deleteS3File", {
        key: file?.file?.s3Key || extractS3KeyFromUrl(file?.url),
        name: file?.file?.name || file?.name,
        eventId: eventProp?._id,
        propertyId: eventProp?.propertyId?._id,
        field: "editedVideos",
      });
      removeFileFromStates(fileId);
      if (res.data.success) showToast("success", "File deleted successfully");
    } catch (error) {
      showToast("error", "Failed to delete file");
    }
  };

  const removeFileFromStates = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
    setUploadQueue((prev) => prev.filter((f) => f.id !== fileId));
    setUploadProgress((prev) => {
      const n = { ...prev };
      delete n[fileId];
      return n;
    });
    setUploadedSizes((prev) => {
      const n = { ...prev };
      delete n[fileId];
      return n;
    });
    setTotalSizes((prev) => {
      const n = { ...prev };
      delete n[fileId];
      return n;
    });
  };

  const [reviewConfirm, setReviewConfirm] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  const sendToReview = async () => {
    try {
      setReviewLoading(true);
      const res = await axios.put(
        `/admin/prepareMarketing/${eventProp?.propertyId?._id}`,
        {
          video: "Review",
          eventId: eventProp?._id,
          eventStatus: eventProp?.status,
        }
      );
      if (res.data.success) {
        showToast("success", "Send to review successfully");
        setReviewConfirm(false);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setReviewLoading(false);
    }
  };

  const renderVideoBlock = (orientation, label) => {
    const hasVideo = files?.some((f) => f.orientation === orientation);
    if (hasVideo) {
      return (
        <div>
          {files
            .filter((f) => f.orientation === orientation)
            .map((fileObj, index) => (
              <VideoContainer
                key={fileObj.id || index}
                video={fileObj.file || fileObj}
                onDelete={() => handleDelete(fileObj.id)}
                viewMode="grid"
                progress={uploadProgress[fileObj.id] || 0}
                showDelete={eventProp?.status !== "completed"}
                uploadedSize={formatBytes(uploadedSizes[fileObj.id] || 0)}
                totalSize={formatBytes(totalSizes[fileObj.id] || 0)}
                eventId={eventProp?._id}
                propertyId={eventProp?.propertyId?._id}
              />
            ))}
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center border border-darkgray border-dashed h-20 relative rounded">
        <input
          type="file"
          id={`video-upload-${orientation}-${eventProp._id}`}
          onChange={(e) => handleFileChange(e, orientation)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          accept="video/*"
        />
        <FaPlus size={12} className="text-darkergray" />
        <p className="text-center mt-1 text-xs text-darkergray">{label}</p>
      </div>
    );
  };

  return (
    <>
      <h4 className="text-sm font-semibold text-black m-0 mb-3">
        Edited Videos
      </h4>
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-3">
        {visibleOrientations.map((orientation) => (
          <React.Fragment key={orientation}>
            {renderVideoBlock(orientation, ORIENTATION_LABELS[orientation])}
          </React.Fragment>
        ))}
      </div>
      {eventProp?.status !== "completed" && files?.length > 0 && (
        <div className="flex justify-end">
          <Button
            onClick={() => {
              const missing = requiredOrientations.filter(
                (o) => !files.some((f) => f.orientation === o),
              );
              if (missing.length > 0) {
                showToast(
                  "error",
                  `Upload ${missing.join(" and ")} video${
                    missing.length > 1 ? "s" : ""
                  }`,
                );
                return;
              }
              setReviewConfirm(true);
            }}
            className="black-button flex"
            disabled={!allVideosUploaded}
          >
            {!allVideosUploaded ? (
              <>
                <i className="fa-solid fa-spinner animate-spin mr-2" />{" "}
                Uploading
              </>
            ) : (
              "Send to Review"
            )}
          </Button>
        </div>
      )}
      <Modal
        isOpen={reviewConfirm}
        onClose={() => setReviewConfirm(false)}
        title="Send to Review Confirmation"
      >
        <div className="flex flex-col space-y-4">
          <p>Are you sure you want to proceed?</p>
          <p className="italic">
            <strong>Note:</strong> Once sent, it cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end">
            <Button
              onClick={() => setReviewConfirm(false)}
              className="gray-button"
            >
              No
            </Button>
            <Button
              onClick={sendToReview}
              className="black-button"
              loading={reviewLoading}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VideoUploadEdit;
